"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Client, Contact, Household, MeetingParticipant, Prospect } from "@/schema"
import { useQueryClient } from "@tanstack/react-query"
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { Ellipsis, Plus } from "lucide-react"
import { toast } from "sonner"

import { useDebounce } from "@/hooks/use-debounce"
import { useLoadingRouter } from "@/hooks/use-loading-router"
import { useCreateClientDialog } from "@/components/clients/use-create-client-dialog"
import { useCreateContactDialog } from "@/components/contacts/use-create-contact-dialog"
import { InformationDialog } from "@/components/dialogs"
import PeekView from "@/components/peek-view"
import { useCreateProspectDialog } from "@/components/prospects/use-create-prospect-dialog"

function deriveNameParts(name?: string | null) {
  if (!name) {
    return { firstName: "", lastName: "" }
  }

  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" }
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  }
}

interface MeetingParticipantAddMenuProps {
  participant: MeetingParticipant
  meetingId?: string
}

export default function MeetingUnknownParticipantMenu({ participant, meetingId }: MeetingParticipantAddMenuProps) {
  const { open: openProspectDialog } = useCreateProspectDialog()
  const { open: openClientDialog } = useCreateClientDialog()
  const { open: openContactDialog } = useCreateContactDialog()
  const { firstName, lastName } = deriveNameParts(participant.name)
  const phone = participant.unformatted_phone ?? undefined
  const queryClient = useQueryClient()
  const { push } = useLoadingRouter()
  const refreshTimeoutRef = useRef<number | undefined>(undefined)

  const scheduleMeetingRefresh = () => {
    if (!meetingId) return

    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current)
    }

    refreshTimeoutRef.current = window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      refreshTimeoutRef.current = undefined
    }, 2000)
  }

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [])

  if (!participant.email) return null

  const handleProspectCreated = (createdProspect: Prospect) => {
    scheduleMeetingRefresh()
    toast.success("Prospect created", {
      action: {
        label: "View prospect",
        onClick: () => {
          push(`/prospects/${createdProspect.id}?secondary=details`)
        },
      },
    })
  }

  const handleClientCreated = (createdClient: Client) => {
    scheduleMeetingRefresh()
    toast.success("Client created", {
      action: {
        label: "View client",
        onClick: () => {
          push(`/clients/${createdClient.id}?secondary=details`)
        },
      },
    })
  }

  const handleContactCreated = (createdContact: Contact) => {
    scheduleMeetingRefresh()
    toast.success("Contact created", {
      action: {
        label: "View contact",
        onClick: () => {
          push(`/contacts/${createdContact.id}`)
        },
      },
    })
  }

  const handleAddProspect = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation()
    openProspectDialog({
      defaultEmail: participant.email ?? "",
      defaultPhone: phone || "",
      defaultFirstName: firstName,
      defaultLastName: lastName,
      onCreated: handleProspectCreated,
    })
  }

  const handleAddClient = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation()
    openClientDialog({
      defaultEmail: participant.email,
      defaultPhone: phone || "",
      defaultFirstName: firstName,
      defaultLastName: lastName,
      onCreated: handleClientCreated,
    })
  }

  const handleAddContact = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation()
    openContactDialog({
      defaultEmail: participant.email,
      defaultPhone: phone || "",
      defaultFirstName: firstName,
      defaultLastName: lastName,
      onCreated: handleContactCreated,
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          className="h-5.5 gap-0.5 pr-1"
          onClick={(event) => event.stopPropagation()}
          disabled={participant.email === "notetaker@slant.app"}
          disableInteraction
        >
          <span>{participant.email}</span>
          {participant.email === "notetaker@slant.app" ? null : <Plus className="size-3!" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="center" className="w-fit" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuItem
          onClick={handleAddProspect}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleAddProspect(event)
            }
          }}
        >
          Create prospect
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleAddClient}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleAddClient(event)
            }
          }}
        >
          Create client
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleAddContact}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleAddContact(event)
            }
          }}
        >
          Create other contact
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface MeetingHouseholdParticipantsProps {
  households?: Household[]
  skinny?: boolean
  className?: string
  emptyLabel?: string
}

export function MeetingHouseholdParticipants({
  households = [],
  skinny = false,
  className,
  emptyLabel,
}: MeetingHouseholdParticipantsProps) {
  if (!households.length) {
    return emptyLabel ? <div className={cn("text-muted-foreground text-sm", className)}>{emptyLabel}</div> : null
  }

  const maxVisible = skinny ? 5 : households.length
  const visibleHouseholds = households.slice(0, maxVisible)
  const remaining = households.length - visibleHouseholds.length
  const peekViewSize = skinny ? "sm" : "base"

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {visibleHouseholds.map((household) => (
        <PeekView
          key={household.id}
          label={household.name}
          recordType={household.type}
          recordId={household.id}
          size={peekViewSize}
        />
      ))}
      {skinny && remaining > 0 && (
        <span className="text-muted-foreground flex items-end">
          <Ellipsis className="h-4 w-4" />
        </span>
      )}
    </div>
  )
}

const GAP_PX = 8
const VIEW_ALL_BUFFER_PX = 80

interface MeetingUnknownParticipantsProps {
  participants?: MeetingParticipant[]
  className?: string
  emptyLabel?: string
  meetingId?: string
}

export function MeetingUnknownParticipants({
  participants = [],
  className,
  emptyLabel,
  meetingId,
}: MeetingUnknownParticipantsProps) {
  const [attendeesOpen, setAttendeesOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const actionableParticipants = useMemo(() => participants.filter((participant) => participant.email), [participants])

  const itemRefs = useMemo(
    () => Array.from({ length: actionableParticipants.length }, () => React.createRef<HTMLDivElement>()),
    [actionableParticipants.length]
  )

  const calculate = useCallback(() => {
    const container = containerRef.current
    if (!container || actionableParticipants.length === 0) {
      setVisibleCount(actionableParticipants.length)
      return
    }
    const containerWidth = container.clientWidth
    if (!containerWidth) {
      setVisibleCount(0)
      return
    }

    let currentWidth = 0
    let count = 0
    for (let i = 0; i < actionableParticipants.length; i++) {
      const el = itemRefs[i].current
      if (!el) continue
      const itemWidth = el.offsetWidth
      const gap = i > 0 ? GAP_PX : 0
      const willOverflow = i < actionableParticipants.length - 1
      const buffer = willOverflow ? VIEW_ALL_BUFFER_PX : 0
      if (currentWidth + itemWidth + gap + buffer > containerWidth) break
      currentWidth += itemWidth + gap
      count++
    }
    setVisibleCount(count)
  }, [actionableParticipants, itemRefs])

  const debouncedCalculate = useDebounce(calculate, 150)

  useEffect(() => {
    calculate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionableParticipants, itemRefs])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const resizeObserver = new ResizeObserver(() => debouncedCalculate())
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [debouncedCalculate])

  if (!actionableParticipants.length) {
    return emptyLabel ? <div className={cn("text-muted-foreground text-sm", className)}>{emptyLabel}</div> : null
  }

  const displayedParticipants =
    visibleCount !== null ? actionableParticipants.slice(0, visibleCount) : actionableParticipants
  const remaining = visibleCount !== null ? actionableParticipants.length - visibleCount : 0

  return (
    <div className={cn("relative w-full overflow-hidden", className)} ref={containerRef}>
      {/* Hidden measurement layer */}
      <div
        style={{
          display: "inline-flex",
          gap: `${GAP_PX}px`,
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          height: 0,
          overflow: "hidden",
          zIndex: -1,
        }}
      >
        {actionableParticipants.map((participant, i) => (
          <div key={participant.email} ref={itemRefs[i]}>
            <Button variant="outline" size="xs" className="h-5.5 gap-0.5 pr-1" disableInteraction>
              <span>{participant.email}</span>
              <Plus className="size-3!" />
            </Button>
          </div>
        ))}
      </div>

      {/* Visible row */}
      <div className="flex items-center gap-x-2">
        {displayedParticipants.map((participant) =>
          participant.email ? (
            <MeetingUnknownParticipantMenu key={participant.email} participant={participant} meetingId={meetingId} />
          ) : null
        )}
        {remaining > 0 && (
          <Button variant="outline" size="xs" className="h-5.5 shrink-0" onClick={() => setAttendeesOpen(true)}>
            View all ({actionableParticipants.length})
          </Button>
        )}
      </div>
      <InformationDialog open={attendeesOpen} onOpenChange={setAttendeesOpen} title="Other attendees">
        <div className="flex flex-col items-start gap-2">
          {actionableParticipants.map((participant) => (
            <MeetingUnknownParticipantMenu key={participant.email} participant={participant} meetingId={meetingId} />
          ))}
        </div>
      </InformationDialog>
    </div>
  )
}
